import React, { useState, useCallback, useEffect } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import { debounce } from "lodash";

// Components
import { ListBoxXnuupPartIdComponent, ID_LOADING, ID_RESULTS_FOUND } from "./components/ListBoxXnuupPartIdComponent";
import { TextFieldXnuupPartIdComponent } from "./components/TextFieldXnuupPartIdComponent";

// Models
import { XnuupPartIdAutoCompleteResponse } from "@/main/models/response";
import { XnuupPartIdAutocompleteFilterRequest } from "@/main/models/request";

// Api's
import { partApi } from "@/main/api";

// Styles
import { useStyles } from "./style";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";

// Utils & Enums
import {
	getDefaultMessageResultsFound,
	getNameElementSelected,
	getTranslateByKey,
	isNullUndefined,
	removeEmptyValues,
} from "@/main/services/utils";
import { IGridData, XnuupPartIdAutoCompleteProps } from "./interfaces";
import { useSearchParams } from "react-router-dom";

export default function XnuupPartIdAutoCompleteComponent({
	watcherCategory,
	watcherSubcategory,
	partPIESName,
	handleChangeXnuupPartId,
	hasBorderStyle,
	hasClearValue,
	setHasClearValue,
	required = true,
	label,
	hasErrorCustom,
	errorMessageCustom,
}: XnuupPartIdAutoCompleteProps) {
	const [searchParams] = useSearchParams();
	const partSearchParams = searchParams.get("partName");

	// Styles
	const classes = useStyles();

	// States
	const INITIAL_GRID_STATE: IGridData = {
		totalItems: 0,
		isLoading: false,
		data: [],
	};
	const [gridData, setGridData] = useState<IGridData>(INITIAL_GRID_STATE);
	const [partIdSelected, setPartIdSelected] = useState<XnuupPartIdAutoCompleteResponse | null>(
		!isNullUndefined(partPIESName) ? ({ partPIESName: partPIESName } as XnuupPartIdAutoCompleteResponse) : null,
	);
	const [inputTextApi, setInputTextApi] = useState<string>("");
	const [inputText, setInputText] = useState<string>("");
	const [pageIndex, setPageIndex] = useState<number>(PaginationEnum.PageIndexIntial);
	const [isLoadingInfiniteScroll, setIsLoadingInfiniteScroll] = useState<boolean>(false);
	const [hasShowMore, setHasShowMore] = useState<boolean>(false);

	// Search term
	useEffect(() => {
		if (inputTextApi && inputTextApi.length >= 3) {
			let optionsData: any[] = [];

			(async () => {
				setGridData({
					isLoading: true,
					totalItems: 0,
					data: [],
				});
				const index = PaginationEnum.PageIndexIntial;
				setPageIndex(index);
				const request: XnuupPartIdAutocompleteFilterRequest = {
					guidPartCategory: watcherCategory ? String(watcherCategory.key) : null,
					guidPartSubCategory: watcherSubcategory ? String(watcherSubcategory.key) : null,
					searchTerm: inputTextApi,
					pageIndex: index,
					pageSize: PaginationEnum.PageSize10,
				};
				const requestFormatted = removeEmptyValues(request);
				const responseApi = await getData(requestFormatted);
				if (responseApi.data.results && responseApi.data.results.length > 0) {
					optionsData = responseApi.data.results;
				}
				setGridData({
					isLoading: false,
					totalItems: responseApi.data.totalNumberOfRecords,
					data: optionsData,
				});
				setHasShowMore(responseApi.data.pageNumber < responseApi.data.totalNumberOfPages);
				setResultsSpan(optionsData.length, responseApi.data.totalNumberOfRecords);
			})();
		}
	}, [inputTextApi]);

	useEffect(() => {
		if (partIdSelected && partPIESName && partPIESName !== "") {
			partIdSelected.partPIESName = partPIESName;
		} else {
			setPartIdSelected(partPIESName ? ({ partPIESName: partPIESName } as XnuupPartIdAutoCompleteResponse) : null);
		}
	}, [partPIESName]);

	useEffect(() => {
		const elementLoading = document.getElementById(ID_LOADING);
		if (elementLoading) {
			if (isLoadingInfiniteScroll) {
				elementLoading.innerText = `${getTranslateByKey("information.searchingXnuupPartId")}...`;
			} else {
				elementLoading.innerText = "";
			}
		}
	}, [isLoadingInfiniteScroll]);

	useEffect(() => {
		if (setHasClearValue && hasClearValue) {
			resetAllState();
			setPartIdSelected(null);
			setHasClearValue(false);
		}
	}, [hasClearValue]);
	useEffect(() => {
		if (partSearchParams) {
			setInputTextApi(partSearchParams);
			setInputText(partSearchParams);
		}
	}, [partSearchParams]);

	const debounceOnChange = useCallback(
		debounce((valueTyping: string) => {
			setInputText(valueTyping);
			setInputTextApi(valueTyping);
		}, 600),
		[],
	);

	function setResultsSpan(dataLength: number, totalItems: number) {
		const element = document.getElementById(ID_RESULTS_FOUND);
		if (element) {
			element.innerText = getDefaultMessageResultsFound(dataLength, totalItems);
		}
	}

	function getSearchTerm() {
		if (partIdSelected !== null && partIdSelected.partPIESName) {
			if (inputText !== "" && partIdSelected.partPIESName !== inputText) {
				return inputText;
			}

			return partIdSelected.partPIESName;
		}

		return inputText;
	}

	// Async' functions
	async function getData(request: XnuupPartIdAutocompleteFilterRequest) {
		return partApi.getXnuupPartId(request);
	}

	// Reset's
	function clearSearchTerms() {
		setInputText("");
		setInputTextApi("");
	}

	function resetAllState() {
		setGridData(INITIAL_GRID_STATE);
		clearSearchTerms();
	}

	function resetStatesScrollInfinity() {
		setPageIndex(PaginationEnum.PageIndexIntial);
		setHasShowMore(false);
	}

	const isNotSelectedPartId = (e: React.SyntheticEvent) =>
		getNameElementSelected(e as React.ChangeEvent<HTMLInputElement>) === "INPUT";

	// Infinite scroll
	async function onLoadInfiniteScroll(request: XnuupPartIdAutocompleteFilterRequest) {
		setIsLoadingInfiniteScroll(true);
		getData(request)
			.then((response) => {
				let resultData: XnuupPartIdAutoCompleteResponse[] = [];
				let resultTotalItems: number = 0;
				if (response.data && response.data.results && response.data.results.length > 0) {
					resultData = response.data.results;
					resultTotalItems = response.data.totalNumberOfRecords;
				}
				if (gridData.data.length > 0) {
					resultData = [...gridData.data, ...resultData];
				}
				setGridData({
					isLoading: false,
					totalItems: resultTotalItems,
					data: resultData,
				});
				// Disable show more
				if (resultTotalItems < PaginationEnum.PageSize10 || resultTotalItems === resultData.length) {
					setHasShowMore(false);
				}
				setResultsSpan(resultData.length, resultTotalItems);
			})
			.finally(() => setIsLoadingInfiniteScroll(false));
	}

	async function onLoadSearchGrid() {
		const index = pageIndex + PaginationEnum.PageIndexIntial;
		setPageIndex(index);
		const request: XnuupPartIdAutocompleteFilterRequest = {
			guidPartCategory: watcherCategory ? String(watcherCategory.key) : null,
			guidPartSubCategory: watcherSubcategory ? String(watcherSubcategory.key) : null,
			searchTerm: inputTextApi,
			pageIndex: index,
			pageSize: PaginationEnum.PageSize10,
		};
		await onLoadInfiniteScroll(request);
	}

	const handleScroll = (event: React.ChangeEvent<HTMLInputElement>) => {
		const listboxNode = event.currentTarget;
		const position = listboxNode.scrollTop + listboxNode.clientHeight;
		if (listboxNode.scrollHeight - position <= 1 && hasShowMore && !isLoadingInfiniteScroll) {
			onLoadSearchGrid();
		}
	};

	return (
		<div id="id-xnuupPartId-autocomplete-container">
			<Autocomplete
				freeSolo={true}
				className={classes.inputAutoComplete}
				noOptionsText={getTranslateByKey("information.noRecordsFound")}
				id="id-xnuupPartId-autocomplete-field"
				size="small"
				loading={gridData.isLoading}
				loadingText={`${getTranslateByKey("information.searchingXnuupPartId")}...`}
				value={partIdSelected}
				filterOptions={(filteredOptions: any[]) => filteredOptions}
				options={gridData.data}
				isOptionEqualToValue={(option, value) => {
					if (!value) return false;
					return option.partGuid === value.partGuid;
				}}
				renderOption={(props, option: XnuupPartIdAutoCompleteResponse) => {
					return <li {...props}>{option.partPIESName}</li>;
				}}
				getOptionLabel={(option: string | XnuupPartIdAutoCompleteResponse) => {
					if (typeof option === "string") return option;
					return option ? option.partPIESName : "";
				}}
				onChange={(_: React.SyntheticEvent, partIdChange: string | XnuupPartIdAutoCompleteResponse | null) => {
					setPartIdSelected(partIdChange as XnuupPartIdAutoCompleteResponse | null);
					handleChangeXnuupPartId(partIdChange as XnuupPartIdAutoCompleteResponse);
				}}
				renderInput={(params) => {
					return (
						<TextFieldXnuupPartIdComponent
							params={params}
							required={required}
							propsCustom={{
								searchTerm: getSearchTerm(),
								label: label ? label : getTranslateByKey("information.xnuupPartId"),
								setInputText,
								hasBorderStyle,
							}}
							hasErrorCustom={hasErrorCustom}
							errorMessageCustom={errorMessageCustom}
						/>
					);
				}}
				onInputChange={(_: React.SyntheticEvent, newInputValue: string, reason: string) => {
					if (reason === "clear") {
						resetAllState();
					} else if (reason === "reset") {
						clearSearchTerms();
					} else if (reason === "input") {
						debounceOnChange(newInputValue);
					}

					// Clear field (Backspace)
					if (newInputValue === "") {
						resetStatesScrollInfinity();
						resetAllState();
					}
				}}
				// Notation: Error on material for event type
				onClose={(e: React.SyntheticEvent) => {
					if (isNotSelectedPartId(e)) {
						resetAllState();
					}

					resetStatesScrollInfinity();
				}}
				{...({
					ListboxProps: {
						onScroll: handleScroll,
						textResultFound: getDefaultMessageResultsFound(gridData.data.length, gridData.totalItems),
						textIsLoading: `${getTranslateByKey("information.searchingXnuupPartId")}...`,
						isLoading: isLoadingInfiniteScroll,
					},
				} as any)}
				ListboxComponent={ListBoxXnuupPartIdComponent}
				disablePortal={true}
			/>
		</div>
	);
}
